import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SetMealIcon from '@mui/icons-material/SetMeal';
import EggIcon from '@mui/icons-material/Egg';
import LocalCafeIcon from '@mui/icons-material/LocalCafe';
import BakeryDiningIcon from '@mui/icons-material/BakeryDining';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import CookieIcon from '@mui/icons-material/Cookie';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import LiquorIcon from '@mui/icons-material/Liquor';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CategoryIcon from '@mui/icons-material/Category';
import { useLang, catName } from '../../../utils/i18n';

// Best-effort icon match based on category name — WooCommerce doesn't give us
// per-category icons, so this is a heuristic, not real data. Still used by
// CategorySidebar.jsx, kept exported here.
export const getCategoryIcon = (name, fontSize = "18px") => {
    const n = name.toLowerCase();
    if (n.includes('fruit') || n.includes('vegetable')) return <LocalFloristIcon sx={{ fontSize }} />;
    if (n.includes('meat') || n.includes('seafood')) return <SetMealIcon sx={{ fontSize }} />;
    if (n.includes('dairy') || n.includes('milk') || n.includes('egg')) return <EggIcon sx={{ fontSize }} />;
    if (n.includes('beverage') || n.includes('drink') || n.includes('juice') || n.includes('tea') || n.includes('coffee')) return <LocalCafeIcon sx={{ fontSize }} />;
    if (n.includes('alcohol') || n.includes('wine') || n.includes('beer') || n.includes('whisky') || n.includes('vodka')) return <LiquorIcon sx={{ fontSize }} />;
    if (n.includes('bakery') || n.includes('bread')) return <BakeryDiningIcon sx={{ fontSize }} />;
    if (n.includes('frozen')) return <AcUnitIcon sx={{ fontSize }} />;
    if (n.includes('snack') || n.includes('biscuit') || n.includes('cookie')) return <CookieIcon sx={{ fontSize }} />;
    if (n.includes('grocery') || n.includes('staple')) return <ShoppingBasketIcon sx={{ fontSize }} />;
    if (n.includes('book')) return <MenuBookIcon sx={{ fontSize }} />;
    return <CategoryIcon sx={{ fontSize }} />;
};

const CategoryNav = () => {
    const { lang } = useLang();
    const [categories, setCategories] = useState([]);
    const [productCount, setProductCount] = useState(null);
    const [showAllCategories, setShowAllCategories] = useState(false);
    const [hoveredCategory, setHoveredCategory] = useState(null);
    const location = useLocation();

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const { data } = await axios.get('/api/v1/categories');
                setCategories(data.categories);
            } catch (error) {
                console.error('Failed to load categories', error);
            }
        };
        const fetchCount = async () => {
            try {
                const { data } = await axios.get('/api/v1/stats/products-count');
                setProductCount(data.count);
            } catch (error) {
                console.error('Failed to load product count', error);
            }
        };
        fetchCategories();
        fetchCount();
    }, []);

    return (
        <div className="hidden sm:block bg-white w-full border-b relative z-10">
            <div className="w-[95%] m-auto flex items-center gap-8 py-3">

                {/* "All Categories" pill trigger + real product count */}
                <div
                    className="relative flex-shrink-0 flex flex-col items-center"
                    onMouseEnter={() => setShowAllCategories(true)}
                    onMouseLeave={() => { setShowAllCategories(false); setHoveredCategory(null); }}
                >
                    <button className="bg-primary-blue text-white pl-5 pr-4 py-2.5 rounded-full flex items-center gap-2 font-medium text-sm whitespace-nowrap">
                        <MenuIcon sx={{ fontSize: "18px" }} />
                        All Categories
                        <ExpandMoreIcon sx={{ fontSize: "18px" }} />
                    </button>
                    {productCount !== null &&
                        <span className="-mt-2 bg-gray-100 text-gray-600 text-xxs px-2 py-0.5 rounded-full border whitespace-nowrap">
                            TOTAL {productCount} PRODUCTS
                        </span>
                    }

                    {showAllCategories && categories.length > 0 && (
                        <div className="absolute left-0 top-full mt-1 w-64 bg-white shadow-2xl border max-h-[70vh] overflow-y-auto">
                            {categories.map((cat) => (
                                <div
                                    key={cat._id}
                                    className="relative"
                                    onMouseEnter={() => setHoveredCategory(cat._id)}
                                >
                                    <Link
                                        to={`/products?category=${cat.slug}`}
                                        className="flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-blue border-b"
                                    >
                                        {catName(cat, lang)}
                                        {cat.children.length > 0 && <ChevronRightIcon sx={{ fontSize: "16px" }} />}
                                    </Link>

                                    {cat.children.length > 0 && hoveredCategory === cat._id && (
                                        <div className="absolute left-full top-0 w-64 bg-white shadow-2xl border max-h-[70vh] overflow-y-auto">
                                            {cat.children.map((sub) => (
                                                <Link
                                                    key={sub._id}
                                                    to={`/products?category=${sub.slug}`}
                                                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-primary-blue border-b"
                                                >
                                                    {catName(sub, lang)}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Static top-level nav — Shop is functional (real product listing),
                    About Us / Contact Us are placeholders until those pages exist */}
                <div className="flex items-center gap-7 text-sm font-medium text-gray-700">
                    <Link to="/" className={`px-3 py-1.5 rounded-full ${location.pathname === '/' ? 'bg-blue-50 text-primary-blue' : 'hover:text-primary-blue'}`}>
                        Home
                    </Link>
                    <span className="text-gray-400 cursor-not-allowed">About Us</span>
                    <Link to="/products" className="hover:text-primary-blue">
                        Shop
                    </Link>
                    <span className="text-gray-400 cursor-not-allowed">Contact Us</span>
                </div>

            </div>
        </div>
    );
};

export default CategoryNav;